import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import styled from 'styled-components';
import { FaTruck } from 'react-icons/fa';

import Loading from '../../../../components/Loading';
import ReasonConfirmModal from '../../../../components/ReasonConfirmModal';
import usePermissions from '../../../../components/Menu/MenuItems/hooks/usePermissions';
import { useUserPdvs } from '../../../../hooks/useUserPdvs';
import {
   cancelShipment,
   cancelTransferOrder,
   dispatchShipment,
   getTransferOrder,
   receiveShipment,
   settleShipmentDivergence,
} from '../../../../services/inventory/transfersApi';
import { notifyApiError } from '../../../../utils/feedback';

import { Container } from '../../../../styles/layout/Container';
import {
   PageHeader,
   PageHeaderActions,
   PageHeaderText,
   PageSubtitle,
   PageTitle,
} from '../../../../styles/layout/PageHeader';
import {
   Section,
   SectionActions,
   SectionBody,
   SectionHeader,
   SectionTitle,
} from '../../../../styles/layout/Section';
import {
   DangerOnDarkButton,
   GhostOnDarkButton,
   PrimaryButton,
} from '../../../../styles/ui/Button';
import { EmptyState } from '../../../../styles/feedback/EmptyState';

import DispatchModal from '../components/DispatchModal';
import GuardedAction from '../components/GuardedAction';
import GeneratedMovements, {
   buildMovementRows,
} from '../components/GeneratedMovements';
import OrderItemsProgress, {
   OrderProgressSummary,
} from '../components/OrderItemsProgress';
import ReceiveModal from '../components/ReceiveModal';
import SettleDivergenceModal from '../components/SettleDivergenceModal';
import ShipmentCard from '../components/ShipmentCard';
import StockWarningsBanner from '../components/StockWarningsBanner';
import {
   OrderStatusBadge,
   PendingDivergenceBadge,
   RoutePill,
} from '../components/StatusBadges';
import {
   CANCEL_REASON_MAX,
   CANCEL_REASON_MIN,
   ORDER_STATUS,
   ORDER_STATUS_CONFIG,
   SHIPMENT_STATUS,
   TRANSFER_PERMISSIONS,
} from '../constants';
import { formatDateTimeBr } from '../utils/format';
import { itemErrorsByProdId } from '../utils/itemErrors';
import { buildOrderProgress } from '../utils/progress';
import { buildPdvAccess } from '../utils/pdvAccess';

// ---------------------------------------------------------------------------
// Detalhe da ordem de transferência (GET /transfer-orders/:id).
//
// É a tela-mãe do fluxo: dela saem expedição, conferência, tratativa e os dois
// cancelamentos. Todas as mutações recarregam a ordem a partir da resposta do
// backend — nada de atualização otimista aqui, porque toda ação move estoque ou
// muda a máquina de estados (e `status_transition` pode fechar a ordem sozinha).
//
// Layout da refatoração visual (2026-09-10, protótipo do handoff de design):
//   · header ESCURO com voltar, pílulas (situação + rota) e as ações de página
//     em variantes on-dark (decisão D1 — nesta tela elas substituem a
//     EntityActionBar, que segue canônica nas demais telas de entidade);
//   · "Expedir remessa" mora no header da seção de Remessas — a ação pertence
//     ao bloco, não à página;
//   · avisos de saldo pós-ação viram faixa âmbar dispensável (D4), nunca
//     confirmação: quando ela aparece, a operação já foi registrada;
//   · quatro blocos empilhados numa coluna: dados → itens (progresso) →
//     remessas (cards colapsáveis) → movimentos gerados.
//
// Habilitação de ação = permissão E estado. As permissões vêm do RBAC
// (`inventory:transfer:*`); os estados, do contrato:
//   · editar pedido → ordem viva (OPEN/PARTIAL);
//   · cancelar ordem → só sem remessa não-cancelada;
//   · expedir → ainda há saldo pendente (`remaining_qtd`);
//   · conferir/cancelar remessa → remessa DISPATCHED;
//   · tratativa → item com divergência sem tratativa.
// ---------------------------------------------------------------------------

const HeaderBlock = styled.div`
   display: grid;
   gap: 10px;
   justify-items: start;
`;

const TitleRow = styled.div`
   display: flex;
   align-items: center;
   gap: 10px;
   flex-wrap: wrap;
`;

const MetaGrid = styled.dl`
   display: grid;
   grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
   gap: 12px 20px;
   margin: 0;

   dt {
      font-size: 11px;
      font-weight: 700;
      text-transform: uppercase;
      letter-spacing: 0.02em;
      color: rgba(0, 0, 0, 0.45);
   }

   dd {
      margin: 3px 0 0;
      font-size: 13.5px;
      color: rgba(0, 0, 0, 0.82);
   }
`;

const NotesBox = styled.div`
   margin-top: 14px;
   padding: 12px 14px;
   border-radius: 10px;
   background: #f7f7f8;
   border: 1px solid ${({ theme }) => theme.colors.borderLight};

   .lbl {
      font-size: 11px;
      font-weight: 700;
      text-transform: uppercase;
      letter-spacing: 0.02em;
      color: rgba(0, 0, 0, 0.45);
   }
   .txt {
      margin-top: 3px;
      font-size: 13px;
      line-height: 1.5;
      color: rgba(0, 0, 0, 0.8);
   }
`;

// Nota de cancelamento DENTRO do cartão claro — translúcido sobre o gradiente
// escuro do shell some (regra do handoff, já quebrou 2×).
const CancelNote = styled.p`
   margin: 0 0 14px;
   padding: 10px 12px;
   border-radius: 8px;
   border: 1px solid rgba(211, 47, 47, 0.3);
   background: rgba(211, 47, 47, 0.08);
   font-size: 12.5px;
   line-height: 1.5;
   color: #8e1b1b;
`;

const ShipmentsMeta = styled.span`
   font-size: 12.5px;
   color: ${({ theme }) => theme.colors.textMuted};
   white-space: nowrap;
`;

// A transição de status devolvida pelas mutações é informação de fluxo: avisa
// que a ordem fechou (ou mudou de faixa) por consequência da ação.
function notifyStatusTransition(transition) {
   if (!transition?.to || transition.to === transition.from) return;
   if (transition.to === ORDER_STATUS.CLOSED) {
      toast.info('Contagem satisfeita — a transferência foi concluída.');
      return;
   }
   toast.info(`Status da transferência: ${transition.to}.`);
}

export default function TransferOrderShow() {
   const { id } = useParams();
   const navigate = useNavigate();
   const location = useLocation();
   const { hasPermission } = usePermissions();

   const canDispatch = hasPermission(TRANSFER_PERMISSIONS.DISPATCH);
   const canReceive = hasPermission(TRANSFER_PERMISSIONS.RECEIVE);
   const canSettle = hasPermission(TRANSFER_PERMISSIONS.SETTLE);
   const canCancel = hasPermission(TRANSFER_PERMISSIONS.CANCEL);
   // Elo com o módulo vizinho: sem esta chave o número do movimento aparece,
   // mas sem link para uma tela que o usuário não abriria.
   const canOpenMovement = hasPermission('inventory:movement:read');

   // Escopo de PDV: barreira VISUAL das ações (o backend continua sendo a
   // autoridade). `isReady` exige carga bem-sucedida — lista vazia por erro de
   // rede NÃO bloqueia nada (fail-open, ver utils/pdvAccess).
   const {
      pdvs: userPdvs,
      isLoading: loadingPdvs,
      error: pdvsError,
   } = useUserPdvs();

   const [order, setOrder] = useState(null);
   const [isLoading, setIsLoading] = useState(true);
   const [isSubmitting, setIsSubmitting] = useState(false);
   const [notFound, setNotFound] = useState(false);

   // Modais — cada um guarda o alvo (ou null quando fechado).
   const [dispatchOpen, setDispatchOpen] = useState(false);
   const [dispatchFieldErrors, setDispatchFieldErrors] = useState({});
   // 422 estruturado por item (handoff 0107 §5) — mapa prod_id → violação,
   // com os valores ATUAIS do servidor (remaining/shipped) para o modal
   // corrigir a referência sem fechar.
   const [dispatchItemErrors, setDispatchItemErrors] = useState({});
   const [receiveItemErrors, setReceiveItemErrors] = useState({});
   const [receiveTarget, setReceiveTarget] = useState(null);
   const [settleTarget, setSettleTarget] = useState(null);
   const [cancelShipmentTarget, setCancelShipmentTarget] = useState(null);
   const [cancelOrderOpen, setCancelOrderOpen] = useState(false);
   const [warnings, setWarnings] = useState(null);

   const fetchOrder = useCallback(async () => {
      setIsLoading(true);
      try {
         const result = await getTransferOrder(id);
         setOrder(result);
         setNotFound(!result);
      } catch (err) {
         setNotFound(true);
         notifyApiError(err, {
            fallback: 'Erro ao carregar a transferência.',
         });
      } finally {
         setIsLoading(false);
      }
   }, [id]);

   useEffect(() => {
      fetchOrder();
   }, [fetchOrder]);

   const handleBack = () => {
      if (location.key !== 'default') navigate(-1);
      else navigate('/inventory/transfers');
   };

   // 409 = o estado mudou por outra mão (versão/estado incompatível, handoff
   // 0107). Além do toast, ressincroniza a tela — sem isto a UI continuaria
   // exibindo o estado que causou o erro.
   const syncIfConflict = (err) => {
      if (err?.kind === 'conflict') fetchOrder();
   };

   // ─── Ações ───────────────────────────────────────────────────────────────

   const handleDispatch = async (payload) => {
      setIsSubmitting(true);
      setDispatchFieldErrors({});
      setDispatchItemErrors({});
      try {
         const result = await dispatchShipment(id, payload);
         toast.success(result.message);
         setDispatchOpen(false);
         setOrder(result.order ?? order);

         const hasWarnings =
            result.stockWarnings.length > 0 ||
            result.transferStockWarnings.length > 0;
         if (hasWarnings) {
            setWarnings({
               stockWarnings: result.stockWarnings,
               transferStockWarnings: result.transferStockWarnings,
               subtitle: `remessa #${result.shipment?.id ?? ''} expedida`,
            });
         }
         // A resposta da mutação já traz a ordem atualizada, mas ela não carrega
         // as remessas antigas com o mesmo detalhe do GET — recarrega para o
         // painel de remessas ficar completo.
         fetchOrder();
      } catch (err) {
         setDispatchFieldErrors(err?.fieldErrors ?? {});
         setDispatchItemErrors(itemErrorsByProdId(err));
         notifyApiError(err, {
            fallback: 'Não foi possível expedir a remessa.',
         });
         syncIfConflict(err);
      } finally {
         setIsSubmitting(false);
      }
   };

   const handleReceive = async (payload) => {
      setIsSubmitting(true);
      setReceiveItemErrors({});
      try {
         const result = await receiveShipment(receiveTarget.id, payload);
         toast.success(result.message);
         setReceiveTarget(null);

         if (result.stockWarnings.length > 0) {
            setWarnings({
               stockWarnings: result.stockWarnings,
               transferStockWarnings: [],
               subtitle: `remessa #${result.shipment?.id ?? ''} conferida`,
            });
         }
         notifyStatusTransition(result.statusTransition);
         fetchOrder();
      } catch (err) {
         setReceiveItemErrors(itemErrorsByProdId(err));
         notifyApiError(err, {
            fallback: 'Não foi possível registrar a conferência.',
         });
         syncIfConflict(err);
      } finally {
         setIsSubmitting(false);
      }
   };

   const handleSettle = async (payload) => {
      setIsSubmitting(true);
      try {
         const result = await settleShipmentDivergence(
            settleTarget.id,
            payload
         );
         toast.success(result.message);
         setSettleTarget(null);
         notifyStatusTransition(result.statusTransition);
         fetchOrder();
      } catch (err) {
         notifyApiError(err, {
            fallback: 'Não foi possível registrar a tratativa.',
         });
         syncIfConflict(err);
      } finally {
         setIsSubmitting(false);
      }
   };

   const handleCancelShipment = async (reason) => {
      setIsSubmitting(true);
      try {
         const result = await cancelShipment(cancelShipmentTarget.id, {
            cancelledReason: reason,
         });
         toast.success(result.message);
         setCancelShipmentTarget(null);
         fetchOrder();
      } catch (err) {
         notifyApiError(err, {
            fallback: 'Não foi possível cancelar a remessa.',
         });
         syncIfConflict(err);
      } finally {
         setIsSubmitting(false);
      }
   };

   const handleCancelOrder = async (reason) => {
      setIsSubmitting(true);
      try {
         const result = await cancelTransferOrder(id, {
            cancelledReason: reason,
         });
         toast.success(result.message);
         setCancelOrderOpen(false);
         setOrder(result.order ?? order);
         fetchOrder();
      } catch (err) {
         notifyApiError(err, {
            fallback: 'Não foi possível cancelar a transferência.',
         });
         syncIfConflict(err);
      } finally {
         setIsSubmitting(false);
      }
   };

   // ─── Derivações de estado ────────────────────────────────────────────────

   // Estabilizados por useMemo: `?? []` cru criaria arrays novos a cada
   // render e furaria a memoização do progresso.
   const items = useMemo(() => order?.items ?? [], [order]);
   const shipments = useMemo(() => order?.shipments ?? [], [order]);

   const orderProgress = useMemo(
      () => buildOrderProgress(items, shipments),
      [items, shipments]
   );

   // Movimentos derivados das remessas (sem request novo). Antes da primeira
   // expedição a lista é vazia — e a seção não é renderizada, porque o estado
   // vazio de "Remessas" já explica que o estoque não se moveu.
   const movementRows = buildMovementRows(shipments);

   const isLive =
      order?.status === ORDER_STATUS.OPEN ||
      order?.status === ORDER_STATUS.PARTIAL;

   const hasPendingQty = items.some((item) => Number(item.remaining_qtd) > 0);

   // "Sem remessa não-cancelada" é a condição do contrato para cancelar a ordem.
   const hasActiveShipment = shipments.some(
      (shipment) => shipment.status !== SHIPMENT_STATUS.CANCELLED
   );

   const shippedUnits = shipments
      .filter((shipment) => shipment.status !== SHIPMENT_STATUS.CANCELLED)
      .reduce(
         (acc, shipment) =>
            acc +
            (shipment.items ?? []).reduce(
               (sum, item) => sum + (Number(item.shipped_qtd) || 0),
               0
            ),
         0
      );

   // Permissão ausente ESCONDE a ação (não é o papel do usuário); acesso de PDV
   // ausente MOSTRA desabilitada com o motivo (é o papel dele, na loja errada).
   const showDispatch = canDispatch && isLive && hasPendingQty;
   const showEdit = canDispatch && isLive;
   const showCancelOrder = canCancel && isLive && !hasActiveShipment;

   const pdvAccess = buildPdvAccess({
      userPdvs,
      isReady: !loadingPdvs && !pdvsError,
      entity: order,
   });

   if (notFound && !isLoading) {
      return (
         <Container>
            <PageHeader>
               <PageHeaderText>
                  <PageTitle>Transferência #{id}</PageTitle>
               </PageHeaderText>
            </PageHeader>
            <Section>
               <SectionBody>
                  <EmptyState
                     title="Transferência não encontrada"
                     description="Verifique o identificador, ou se você tem acesso a um dos PDVs envolvidos."
                  />
               </SectionBody>
            </Section>
         </Container>
      );
   }

   const statusLabel = ORDER_STATUS_CONFIG[order?.status]?.label ?? '—';

   return (
      <Container>
         <Loading isLoading={isLoading || isSubmitting} />

         <PageHeader>
            <PageHeaderText>
               <HeaderBlock>
                  <GhostOnDarkButton type="button" onClick={handleBack}>
                     ← Voltar para a lista
                  </GhostOnDarkButton>
                  <TitleRow>
                     <PageTitle>Transferência #{order?.id ?? id}</PageTitle>
                     <OrderStatusBadge value={order?.status} onDark />
                     <RoutePill
                        originName={order?.origin_pdv_name}
                        destName={order?.dest_pdv_name}
                     />
                     {order?.has_pending_divergence && (
                        <PendingDivergenceBadge onDark />
                     )}
                  </TitleRow>
                  <PageSubtitle>
                     Criada por {order?.created_by_name ?? '—'} em{' '}
                     {formatDateTimeBr(order?.created_at)}
                  </PageSubtitle>
               </HeaderBlock>
            </PageHeaderText>

            {/* Ações de página nas variantes on-dark (D1). "Expedir remessa"
                não mora aqui — pertence à seção de Remessas. */}
            <PageHeaderActions>
               {showCancelOrder && (
                  <GuardedAction reason={pdvAccess.blockedForOrigin}>
                     <DangerOnDarkButton
                        type="button"
                        onClick={() => setCancelOrderOpen(true)}
                        disabled={isSubmitting}
                     >
                        Cancelar transferência
                     </DangerOnDarkButton>
                  </GuardedAction>
               )}
               {showEdit && (
                  <GuardedAction reason={pdvAccess.blockedForOrigin}>
                     <GhostOnDarkButton
                        type="button"
                        onClick={() =>
                           navigate(`/inventory/transfers/${order.id}/edit`)
                        }
                        disabled={isSubmitting}
                     >
                        ✎ Editar pedido
                     </GhostOnDarkButton>
                  </GuardedAction>
               )}
            </PageHeaderActions>
         </PageHeader>

         {warnings && (
            <StockWarningsBanner
               subtitle={warnings.subtitle}
               stockWarnings={warnings.stockWarnings}
               transferStockWarnings={warnings.transferStockWarnings}
               onDismiss={() => setWarnings(null)}
            />
         )}

         <Section>
            <SectionHeader>
               <SectionTitle>Dados da transferência</SectionTitle>
               <SectionActions />
            </SectionHeader>
            <SectionBody>
               {order?.status === ORDER_STATUS.CANCELLED && (
                  <CancelNote>
                     Cancelada em {formatDateTimeBr(order.cancelled_at)} por{' '}
                     {order.cancelled_by_name ?? '—'}.
                     {order.cancelled_reason
                        ? ` Motivo: ${order.cancelled_reason}`
                        : ''}
                  </CancelNote>
               )}

               <MetaGrid>
                  <div>
                     <dt>Origem</dt>
                     <dd>{order?.origin_pdv_name ?? '—'}</dd>
                  </div>
                  <div>
                     <dt>Destino</dt>
                     <dd>{order?.dest_pdv_name ?? '—'}</dd>
                  </div>
                  <div>
                     <dt>Criada por</dt>
                     <dd>{order?.created_by_name ?? '—'}</dd>
                  </div>
                  <div>
                     <dt>Criada em</dt>
                     <dd>{formatDateTimeBr(order?.created_at)}</dd>
                  </div>
                  <div>
                     <dt>Concluída em</dt>
                     <dd>{formatDateTimeBr(order?.closed_at)}</dd>
                  </div>
                  <div>
                     <dt>Situação</dt>
                     <dd>{statusLabel}</dd>
                  </div>
               </MetaGrid>

               {order?.notes && (
                  <NotesBox>
                     <div className="lbl">Observações</div>
                     <div className="txt">{order.notes}</div>
                  </NotesBox>
               )}
            </SectionBody>
         </Section>

         <Section>
            <SectionHeader>
               <SectionTitle>Itens do pedido</SectionTitle>
               <SectionActions>
                  {items.length ? `${items.length} item(ns)` : ''}
               </SectionActions>
            </SectionHeader>
            <SectionBody>
               {items.length === 0 ? (
                  <EmptyState title="Sem itens" description="—" />
               ) : (
                  <>
                     <OrderProgressSummary progress={orderProgress} />
                     <OrderItemsProgress items={items} shipments={shipments} />
                  </>
               )}
            </SectionBody>
         </Section>

         <Section>
            <SectionHeader>
               <SectionTitle>Remessas</SectionTitle>
               <SectionActions>
                  {shipments.length > 0 && (
                     <ShipmentsMeta>
                        {shipments.length} remessa(s) · {shippedUnits} un
                        expedidas
                     </ShipmentsMeta>
                  )}
                  {showDispatch && (
                     <GuardedAction reason={pdvAccess.blockedForOrigin}>
                        <PrimaryButton
                           type="button"
                           onClick={() => setDispatchOpen(true)}
                           disabled={isSubmitting}
                        >
                           <FaTruck size={13} aria-hidden="true" /> Expedir
                           remessa
                        </PrimaryButton>
                     </GuardedAction>
                  )}
               </SectionActions>
            </SectionHeader>
            <SectionBody>
               {shipments.length === 0 ? (
                  <EmptyState
                     icon={<FaTruck size={20} />}
                     title="Nada expedido ainda"
                     description={`O estoque está reservado na origem: sai do disponível, mas continua no físico de ${order?.origin_pdv_name ?? 'origem'}. Use "Expedir remessa" para iniciar o transporte.`}
                  />
               ) : (
                  shipments.map((shipment) => (
                     <ShipmentCard
                        key={shipment.id}
                        shipment={shipment}
                        canReceive={canReceive}
                        canCancel={canCancel}
                        canSettle={canSettle}
                        pdvAccess={pdvAccess}
                        onReceive={setReceiveTarget}
                        onCancel={setCancelShipmentTarget}
                        onSettle={setSettleTarget}
                     />
                  ))
               )}
            </SectionBody>
         </Section>

         {movementRows.length > 0 && (
            <Section>
               <SectionHeader>
                  <SectionTitle>Movimentos de estoque gerados</SectionTitle>
                  <SectionActions>
                     {`${movementRows.length} movimento(s)`}
                  </SectionActions>
               </SectionHeader>
               <SectionBody>
                  <GeneratedMovements
                     rows={movementRows}
                     canOpenMovement={canOpenMovement}
                  />
               </SectionBody>
            </Section>
         )}

         <DispatchModal
            isOpen={dispatchOpen}
            order={order}
            isSubmitting={isSubmitting}
            fieldErrors={dispatchFieldErrors}
            itemErrors={dispatchItemErrors}
            onCancel={() => {
               setDispatchOpen(false);
               setDispatchFieldErrors({});
               setDispatchItemErrors({});
            }}
            onConfirm={handleDispatch}
         />

         <ReceiveModal
            isOpen={!!receiveTarget}
            shipment={receiveTarget}
            isSubmitting={isSubmitting}
            itemErrors={receiveItemErrors}
            onCancel={() => {
               setReceiveTarget(null);
               setReceiveItemErrors({});
            }}
            onConfirm={handleReceive}
         />

         <SettleDivergenceModal
            isOpen={!!settleTarget}
            shipment={settleTarget}
            isSubmitting={isSubmitting}
            onCancel={() => setSettleTarget(null)}
            onConfirm={handleSettle}
         />

         <ReasonConfirmModal
            isOpen={cancelOrderOpen}
            title="Cancelar transferência"
            subtitle={`Transferência #${order?.id ?? id}`}
            infoText="A transferência é encerrada e a reserva de estoque na origem é liberada. Só é possível cancelar enquanto não houver remessa expedida."
            infoTone="danger"
            reasonLabel="Motivo do cancelamento"
            placeholder="Descreva o motivo (mín. 8 caracteres)"
            minLength={CANCEL_REASON_MIN}
            maxLength={CANCEL_REASON_MAX}
            confirmLabel="Cancelar transferência"
            submittingLabel="Cancelando…"
            cancelLabel="Voltar"
            isSubmitting={isSubmitting}
            inputId="cancel-order-reason"
            onCancel={() => setCancelOrderOpen(false)}
            onConfirm={handleCancelOrder}
         />

         <ReasonConfirmModal
            isOpen={!!cancelShipmentTarget}
            title="Cancelar remessa em trânsito"
            subtitle={
               cancelShipmentTarget
                  ? `Remessa #${cancelShipmentTarget.id}`
                  : null
            }
            infoText="O estoque desta remessa volta para o PDV de origem por um movimento de reversão."
            infoTone="danger"
            reasonLabel="Motivo do cancelamento"
            placeholder="Descreva o motivo (mín. 8 caracteres)"
            minLength={CANCEL_REASON_MIN}
            maxLength={CANCEL_REASON_MAX}
            confirmLabel="Cancelar remessa"
            submittingLabel="Cancelando…"
            cancelLabel="Voltar"
            isSubmitting={isSubmitting}
            inputId="cancel-shipment-reason"
            onCancel={() => setCancelShipmentTarget(null)}
            onConfirm={handleCancelShipment}
         />
      </Container>
   );
}
